import { useEffect, useState } from "react";
import Logo from "../../images/header-logo-captify.svg";
import { formatDashboardDateMedium } from "../../helpers/dashboardTime";
import "./mcpAuthorization.css";
import { isMcpAuthorizationReturnUrl } from './mcpRedirect.mjs';

const errorMessages = {
  MCP_AUTHORIZATION_EXPIRED: "This authorization request has expired. Start the connection again in ChatGPT.",
  MCP_ACCOUNT_CHANGED_RESTART_AUTHORIZATION: "The signed-in account changed. Start a new connection in ChatGPT.",
  MCP_INVALID_CONSENT: "This page is out of date. Reload it before authorizing.",
  MCP_ACCOUNT_NOT_APPROVED: "This account is not approved for the private Captify connection.",
  MCP_ORDERS_FORBIDDEN: "This account does not have permission to read orders.",
  MCP_ACCOUNT_UNAVAILABLE: "This account is unavailable. Please sign in again.",
};
const apiBase = () => (import.meta.env.VITE_BASE_URL || "").replace(/\/$/, "");
async function request(path, options = {}) {
  const token = localStorage.getItem("authToken");
  const headers = { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  const send = value => fetch(`${apiBase()}/mcp/oauth/${path}`, {
    ...options, headers: value, credentials: "include", cache: "no-store", referrerPolicy: "no-referrer",
  });
  let response = await send(headers);
  // MCP-specific login uses the existing server-side session, without sending a dashboard JWT through a URL.
  if (response.status === 401 && token) response = await send({ "Content-Type": "application/json" });
  const body = await response.json();
  if (!response.ok) {
    const error = new Error(errorMessages[body.error] || "Unable to complete the request. Please try again.");
    error.needsLogin = response.status === 401;
    throw error;
  }
  return body;
}

export default function McpAuthorizationPage() {
  const connections = window.location.pathname === "/mcp/connections";
  const ticket = new URLSearchParams(window.location.search).get("request") || "";
  const validTicket = /^[a-f0-9]{64}$/.test(ticket);
  const [view, setView] = useState(null);
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [needsLogin, setNeedsLogin] = useState(false);
  const [confirmRevoke, setConfirmRevoke] = useState(null);
  const date = (value) => formatDashboardDateMedium(value, "");

  useEffect(() => {
    document.title = connections ? "Captify | AI connections" : "Captify | Connect to ChatGPT";
    let active = true;
    if (!connections && !validTicket) {
      setError("Open this page by connecting Captify in ChatGPT."); setLoading(false); return;
    }
    request(connections ? "grants" : `consent/${ticket}`)
      .then(body => { if (active) { connections ? setGrants(body.grants) : setView(body); } })
      .catch(reason => { if (active) { setNeedsLogin(Boolean(reason.needsLogin)); setError(reason.message); } })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [connections, ticket, validTicket]);

  async function decide(allow) {
    setBusy(true); setError("");
    try {
      const body = await request(`consent/${ticket}`, { method: "POST", body: JSON.stringify({ allow, csrf: view.csrf }) });
      if (typeof body.redirectUrl !== "string") throw new Error("Authorization did not complete. Please start the connection again in ChatGPT.");
      const target = new URL(body.redirectUrl);
      if (!isMcpAuthorizationReturnUrl(body.redirectUrl)) {
        throw new Error("The authorization return address is invalid.");
      }
      window.location.assign(target.href);
    } catch (reason) { setError(reason.message); setBusy(false); }
  }

  async function revoke(id) {
    setBusy(true); setError("");
    try {
      await request(`grants/${id}`, { method: "DELETE" });
      const body = await request("grants"); setGrants(body.grants); setConfirmRevoke(null);
    } catch (reason) { setError(reason.message); }
    finally { setBusy(false); }
  }

  return <main className="mcp-page">
    <section className="mcp-card" aria-labelledby="mcp-title">
      <img className="mcp-logo" src={Logo} alt="Captify" />
      <p className="mcp-eyebrow">PRIVATE COMPANY CONNECTION</p>
      <h1 id="mcp-title">{connections ? "Connected AI apps" : "Connect Captify to ChatGPT"}</h1>
      <p className="mcp-intro">{connections ? "Manage access granted from your Captify account." : "Review the access you are granting before continuing."}</p>
      {loading && <p role="status">Loading your connection…</p>}
      {error && <p className="mcp-error" role="alert">{needsLogin ? "Sign in to Captify to continue." : error}</p>}
      {needsLogin && <a className="mcp-button mcp-primary" href={connections ? "/login" : `${apiBase()}/mcp/oauth/login?request=${ticket}`}>Sign in with Captify</a>}
      {view && !connections && <>
        <div className="mcp-account"><span>Signed in as</span><strong>{view.user.email || view.user.name || "Captify user"}</strong></div>
        <h2>Read-only access</h2>
        <ul className="mcp-scopes">
          <li>Order details, production status and supplier history</li>
          <li>Patient prescriptions, telephone numbers and full delivery addresses</li>
        </ul>
        <div className="mcp-duration"><strong>Valid for 6 months</strong><span>You can revoke access at any time. Your current order permissions still apply.</span></div>
        <p className="mcp-note">ChatGPT cannot create, change, cancel or send orders through this connection.</p>
        {!view.canAuthorize && <p className="mcp-error" role="alert">This account is not approved or does not have order-reading permission. Contact your dashboard administrator.</p>}
        <div className="mcp-actions">
          <button className="mcp-button" disabled={busy} onClick={() => decide(false)}>Decline</button>
          <button className="mcp-button mcp-primary" disabled={busy || !view.canAuthorize} onClick={() => decide(true)}>{busy ? "Please wait…" : "Allow access"}</button>
        </div>
      </>}
      {connections && !loading && !needsLogin && <div className="mcp-grants">
        {!grants.length && !error && <p>No AI connections have been authorized.</p>}
        {grants.map(grant => {
          const active = !grant.revokedAt && new Date(grant.expiresAt).getTime() > Date.now();
          return <article className="mcp-grant" key={grant.id}>
            <div><h2>{grant.clientName}</h2><span className={active ? "mcp-active" : "mcp-muted"}>{grant.revokedAt ? "Revoked" : active ? "Active" : "Expired"}</span></div>
            <p>Authorized {date(grant.createdAt)} · Expires {date(grant.expiresAt)}</p>
            {active && (confirmRevoke === grant.id ? <div className="mcp-revoke-confirm"><p>ChatGPT will immediately lose access through this connection.</p><div className="mcp-actions"><button className="mcp-button" disabled={busy} onClick={() => setConfirmRevoke(null)}>Keep access</button><button className="mcp-button mcp-danger" disabled={busy} onClick={() => revoke(grant.id)}>Revoke access</button></div></div> : <button className="mcp-button" onClick={() => setConfirmRevoke(grant.id)}>Revoke access</button>)}
          </article>;
        })}
      </div>}
      <footer className="mcp-footer"><a href="/">Back to Dashboard</a></footer>
    </section>
  </main>;
}
